import {
  FREQUENCY_OPTIONS,
  WEEKDAY_OPTIONS,
  cronToSchedule,
  scheduleToCron,
  describeSchedule,
  formatUtcTime,
  parseTimeInput,
  type FriendlySchedule,
  type ScheduleFrequency,
} from './schedule-cron';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface ScheduleBuilderProps {
  cron: string;
  onChange: (cron: string) => void;
}

function applyPatch(current: FriendlySchedule, patch: Partial<FriendlySchedule>): FriendlySchedule {
  const next = { ...current, ...patch };
  if (next.frequency !== 'custom') {
    next.cron = scheduleToCron(next);
  }
  return next;
}

export function ScheduleBuilder({ cron, onChange }: ScheduleBuilderProps) {
  const schedule = cronToSchedule(cron);

  const commit = (patch: Partial<FriendlySchedule>) => {
    const next = applyPatch(schedule, patch);
    onChange(scheduleToCron(next));
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-xs">Runs</Label>
        <Select
          value={schedule.frequency}
          onValueChange={(v) => {
            const frequency = v as ScheduleFrequency;
            if (frequency === 'custom') {
              commit({
                frequency,
                cron: schedule.cron.trim() || scheduleToCron({ ...schedule, frequency: 'daily' }),
              });
              return;
            }
            commit({ frequency });
          }}
        >
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="How often?" />
          </SelectTrigger>
          <SelectContent>
            {FREQUENCY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {schedule.frequency !== 'custom' && (
        <div className="space-y-2">
          <Label className="text-xs">Time (MUT)</Label>
          <Input
            type="time"
            className="h-9 text-sm"
            value={formatUtcTime(schedule.hour, schedule.minute)}
            onChange={(e) => {
              const parsed = parseTimeInput(e.target.value);
              if (!parsed) return;
              commit({ hour: parsed.hour, minute: parsed.minute });
            }}
          />
        </div>
      )}

      {schedule.frequency === 'weekly' && (
        <div className="space-y-2">
          <Label className="text-xs">Day of week</Label>
          <Select
            value={String(schedule.dayOfWeek)}
            onValueChange={(v) => commit({ dayOfWeek: Number(v) })}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Select day" />
            </SelectTrigger>
            <SelectContent>
              {WEEKDAY_OPTIONS.map((d) => (
                <SelectItem key={d.value} value={String(d.value)}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {schedule.frequency === 'monthly' && (
        <div className="space-y-2">
          <Label className="text-xs">Day of month</Label>
          <Select
            value={String(schedule.dayOfMonth)}
            onValueChange={(v) => commit({ dayOfMonth: Number(v) })}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Select day" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                <SelectItem key={day} value={String(day)}>
                  {day}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">
            Days 1–28 are offered so the schedule runs every month.
          </p>
        </div>
      )}

      {schedule.frequency === 'custom' && (
        <div className="space-y-2">
          <Label className="text-xs">Cron expression (MUT)</Label>
          <Input
            className="h-9 text-sm font-mono"
            placeholder="e.g. 0 8 * * 1"
            value={schedule.cron}
            onChange={(e) => onChange(e.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">
            Advanced: minute hour day month weekday in Mauritius Time.
          </p>
        </div>
      )}

      <p className="rounded-md bg-muted/50 px-2.5 py-2 text-[11px] leading-snug text-muted-foreground">
        {describeSchedule(schedule)}
      </p>
    </div>
  );
}
